import { useState } from 'react';
import Modal from '@components/widgets/Modal';
import Input from '@components/forms/Input';
import Button from '@components/forms/Button';
import { isDomainMatch } from '@utils';
import { ADD_BROKER_USER, post } from '@services';
import { toBrokerOrgMember, type ApiBrokerUser } from '../mapBrokerOrg';
import type { BrokerOrgMember } from '@types';

interface Props {
  open: boolean;
  orgDomain: string;
  organisationId: string;
  onClose: () => void;
  onInviteSent: (member: BrokerOrgMember, user: ApiBrokerUser) => void;
}

const InviteMemberModal = ({ open, orgDomain, organisationId, onClose, onInviteSent }: Props) => {
  const [email, setEmail] = useState('');
  const [domainError, setDomainError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleClose = () => {
    setEmail(''); setDomainError(''); setSuccessMessage('');
    onClose();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (domainError) setDomainError('');
  };

  const handleSubmit = async () => {
    if (!email) { setDomainError('Please enter an email address.'); return; }
    if (orgDomain && !isDomainMatch(email, orgDomain)) {
      setDomainError(`Only brokers with a @${orgDomain} email can be invited.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await post(ADD_BROKER_USER, {
        brokerOrganisationId: organisationId,
        email,
        roles: ['STANDARD']
      });
      const brokerUser = response?.brokerUser as ApiBrokerUser | undefined;
      if (!brokerUser?.userId) {
        setDomainError('The invitation could not be sent.');
        return;
      }
      setSuccessMessage(`Invite sent to ${email}`);
      onInviteSent(toBrokerOrgMember(brokerUser), brokerUser);
      setTimeout(handleClose, 1500);
    } catch (error) {
      console.error('Error inviting broker organisation member:', error);
      setDomainError('The invitation could not be sent.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal title="Invite Member" size="md" open={open} onClose={handleClose}>
      <div className="flex flex-col gap-4 p-2">
        <div>
          <p className="text-sm font-medium text-grey-700 mb-1.5">Email address</p>
          <Input type="email" placeholder={orgDomain ? `e.g. colleague@${orgDomain}` : 'Email address'} value={email} onChange={handleEmailChange} />
          {domainError && <p className="text-error-500 text-xs mt-1">{domainError}</p>}
          {orgDomain && <p className="text-xs text-grey-500 mt-1">Must use a <strong>@{orgDomain}</strong> email address.</p>}
        </div>
        <div className="h-5 text-right">
          {successMessage && <p className="text-green-600 text-sm font-medium">{successMessage}</p>}
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" className="border border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 bg-white" onClick={handleClose}>Cancel</Button>
          <Button className="bg-primary text-white normal-case font-semibold rounded-lg text-sm py-2 px-4" onClick={handleSubmit} disabled={isSubmitting}>Send Invite</Button>
        </div>
      </div>
    </Modal>
  );
};

export default InviteMemberModal;
